import { useEffect, useState } from 'react';

import {
    ColumnNames,
    ColumnType,
    TaxCardMainTable,
} from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/TaxCardMainTable';

import { EmployeeTaxcardsInfo } from '@Page/SalaryTaxCard/hooks/useSalaryTaxCardForReal';
import TaxCardMainTableToolbar from '@Page/SalaryTaxCard/component/SalaryTaxCardMainTable/TaxCardMainTableToolbar';
import { ActionBar } from '@Page/SalaryTaxCard/component/ActionBar/ActionBar';
import { CardVariant, StatusBar, StatusBarTexts } from '@Page/SalaryTaxCard/component/StatusBar/StatusBar';
import { AltinnData, useAltinnHandler } from '@Page/SalaryTaxCard/reducers/altinnHandling';
import { EmployeeDTO } from '@Page/SalaryTaxCard/SalaryTaxCard.types';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePageTitle } from '@Hooks/usePageTitle';
import { PageContent, PageHeaderTitle, PageWrapper } from '@Component/Page/Page';
import { createOrdinalFromArray } from '@Util/misc.ts';
import { TaxcardSelectableMachinery } from './hooks/useTaxcardSelectableEmployees';
import { useEmployeeData } from './hooks/useEmployeeData';

export const PREBEN_COMPANYID = 3759310; // Preben Gjerdrum Revisjon
export const GRONN_TIGER = 44336218;
export const CYPRESS_TESTING_TAXCARDS = false;

export function columnsWeWant(employees: EmployeeDTO[]): string[] {
    const all = Object.keys(ColumnNames).map((key) => ColumnNames[key as ColumnType]);
    // const all = Object.keys(ColumnNames).map((key: keyof typeof ColumnNames) => ColumnNames[key]); // does not work
    if (
        employees.every(
            (employee) =>
                !employee.taxcard ||
                employee.taxcard?.additionalInfo === undefined ||
                employee.taxcard?.additionalInfo == '',
        )
    ) {
        all.splice(all.indexOf(ColumnNames.TAXCARD_ADDITIONAL_INFO), 1);
    }
    return all;
}

export function SalaryTaxCard() {
    const altinn: AltinnData = useAltinnHandler();

    const intl = useIntl();
    const pageTitle = intl.formatMessage({ id: 'text_tax_deduction_card' });
    usePageTitle(pageTitle);

    const today = new Date();
    //First day you get to order next years cards - according to TaxDeductionCardsOrderer.java
    const firstOrderMonthDay = new Date(today.getFullYear(), 11, 15);

    const latestAllowedYear = firstOrderMonthDay < today ? today.getFullYear() + 1 : today.getFullYear();

    const [year, setYear] = useState(latestAllowedYear);

    const [showHasQuit, setShowHasQuit] = useState(false);

    const [search, setSearch] = useState('');

    const [sort, setSort] = useState('');
    const toggleSort = (string: string) => {
        const [currentSort, currentSortOrder] = sort.split('_', 2);
        if (currentSort == string) {
            switch (currentSortOrder) {
                case 'ASC':
                    setSort('');
                    break;
                case 'DESC':
                    //displayname doesnt need _asc as that is default
                    if (string === ColumnNames.EMPLOYEE_NAME) {
                        setSort('');
                    } else {
                        setSort(currentSort + '_ASC');
                    }
                    break;
            }
        } else {
            setSort(string + '_DESC');
        }
    };

    // Both (lazy partial) loaded employees (and quit's) names and taxcard data, and the id's of all electable employees
    const allEmployeeData = useEmployeeData(year, search, sort, showHasQuit, altinn);

    const [fetchTaxcardsPopupOpen, setFetchTaxcardsPopupOpen] = useState(false);

    return (
        <PageWrapper>
            <PageHeaderTitle>
                <FormattedMessage id={'text_tax_deduction_card'} />
            </PageHeaderTitle>
            <PageContent>
                <SalaryTaxCardInner
                    search={search}
                    setSearch={setSearch}
                    employeeTaxcards={allEmployeeData.employeesTaxcards}
                    quitEmployeeTaxcards={allEmployeeData.quitEmployeesTaxcards}
                    showHasQuit={showHasQuit}
                    setShowHasQuit={setShowHasQuit}
                    latestAllowedYear={latestAllowedYear}
                    year={year}
                    setYear={setYear}
                    altinn={altinn}
                    sort={sort}
                    toggleSort={toggleSort}
                    fetchTaxcardsPopupOpen={fetchTaxcardsPopupOpen}
                    setFetchTaxcardsPopupOpen={setFetchTaxcardsPopupOpen}
                    taxcardSelectEmployeesMachinery={allEmployeeData.taxcardSelectEmployeesMachinery}
                    taxcardSelectQuitEmployeesMachinery={allEmployeeData.taxcardSelectQuitEmployeesMachinery}
                />
            </PageContent>
        </PageWrapper>
    );
}

export interface SalaryTaxCardProps {
    search: string;
    setSearch?: React.Dispatch<React.SetStateAction<string>>;
    employeeTaxcards: EmployeeTaxcardsInfo;
    quitEmployeeTaxcards?: EmployeeTaxcardsInfo;
    showHasQuit: boolean;
    setShowHasQuit?: React.Dispatch<React.SetStateAction<boolean>>;
    latestAllowedYear: number;
    year: number;
    setYear?: React.Dispatch<React.SetStateAction<number>>;
    altinn: AltinnData;
    sort: string;
    toggleSort: (x: string) => void;
    mockSkeleton?: boolean;
    fetchTaxcardsPopupOpen: boolean;
    setFetchTaxcardsPopupOpen: React.Dispatch<React.SetStateAction<boolean>>;
    taxcardSelectEmployeesMachinery: TaxcardSelectableMachinery;
    taxcardSelectQuitEmployeesMachinery: TaxcardSelectableMachinery;
}

export function SalaryTaxCardInner(props: SalaryTaxCardProps) {
    const [skeleton, setSkeleton] = useState(props.mockSkeleton ?? false);

    useEffect(() => {
        if (props.altinn.receivedCards) {
            setSkeleton(true);
            setTimeout(() => {
                setSkeleton(false);
            }, 1000);
        }
    }, [props.altinn.receivedCards]);

    const intl = useIntl();
    // Trying to put the hook and usage of it in StatusBar gave me hooks-reordered warning from react during runtime. Could not see anything wrong regarding conditional usage of hooks. [Kristoffer Kvello, 2023-11-17]
    const statusBarTexts: StatusBarTexts = {
        heading_unexpected_error: intl.formatMessage({
            id: 'heading_unexpected_error',
        }),
        text_session_valid_until: intl.formatMessage({
            id: 'text_session_valid_until',
        }),
        text_last_updated_in_tripletex: intl.formatMessage({
            id: 'text_last_updated_in_tripletex',
        }),
        message_awaiting_response_from: intl.formatMessage({
            id: 'message_awaiting_response_from',
        }),
        text_altinn: intl.formatMessage({ id: 'text_altinn' }),
        message_taxcards_received: intl.formatMessage({
            id: 'message_taxcards_received',
        }),
        text_csv_qualifier_none: intl.formatMessage({
            id: 'text_csv_qualifier_none',
        }),
        message_taxcards_received_singular: intl.formatMessage({
            id: 'message_taxcards_received_singular',
        }),
        message_taxcards_received_plural: intl.formatMessage(
            {
                id: 'message_taxcards_received',
            },
            createOrdinalFromArray([props.altinn.numCardsReceived ?? 0]),
        ),
        text_altinn_login_required: intl.formatMessage({
            id: 'text_altinn_login_required',
        }),
        message_taxcards_no_taxcard_received: intl.formatMessage({
            id: 'message_taxcards_no_taxcard_received',
        }),
        message_taxcards_try_again: intl.formatMessage({
            id: 'message_taxcards_try_again',
        }),
    };

    const statusBar = (
        <StatusBar
            status={getStatus(props.employeeTaxcards, props.altinn)}
            texts={statusBarTexts}
            altinn={props.altinn}
        />
    );

    const actionBar = (
        <ActionBar
            showHasQuit={props.showHasQuit}
            setShowHasQuit={props.setShowHasQuit}
            fetchTaxcardsPopupOpen={props.fetchTaxcardsPopupOpen}
            setFetchTaxcardsPopupOpen={props.setFetchTaxcardsPopupOpen}
            year={props.year}
            setYear={props.setYear}
            altinn={props.altinn}
            latestAllowedYear={props.latestAllowedYear}
            // need to check if user still wants to have cards for quit employees (might have selected and then changed mind)
            allSelectedEmployees={
                props.showHasQuit
                    ? props.taxcardSelectEmployeesMachinery.state.selectedEmployees.concat(
                          props.taxcardSelectQuitEmployeesMachinery.state.selectedEmployees,
                      )
                    : props.taxcardSelectEmployeesMachinery.state.selectedEmployees
            }
        />
    );

    const tableContainer = (
        <div className="atl-table-container">
            <TaxCardMainTableToolbar
                search={props.search}
                setSearch={props.setSearch}
                setOpen={props.setFetchTaxcardsPopupOpen}
                altinn={props.altinn}
            />
            <TaxCardMainTable
                columns={columnsWeWant(props.employeeTaxcards.data)}
                employeeTaxcards={props.employeeTaxcards}
                quitEmployeeTaxcards={props.showHasQuit ? props.quitEmployeeTaxcards : undefined}
                sort={props.sort}
                toggleSort={props.toggleSort}
                skeleton={skeleton}
                lastUpdatedOrderId={props.altinn.lastUpdatedOrderId}
                taxcardSelectEmployeesMachinery={props.taxcardSelectEmployeesMachinery}
                taxcardSelectQuitEmployeesMachinery={props.taxcardSelectQuitEmployeesMachinery}
            />
        </div>
    );

    return (
        <div className="tlx-salaryTaxCard">
            <div>
                {actionBar}
                {statusBar}
                {tableContainer}
            </div>
        </div>
    );
}

export function SalaryTaxCardPage() {
    return <SalaryTaxCard />;
}

//What maps to what status card?
function getStatus(taxcards: EmployeeTaxcardsInfo, altinn: AltinnData): CardVariant {
    if (altinn.isOlderWaitingTaxcardApplications && !altinn.isLoggedInUntil) {
        return CardVariant.Warning_login_altinn;
    }
    if (altinn.receivedNoCardsMustRetryFetch) {
        return CardVariant.Warning_retry_fetch;
    }
    if (altinn.receivedCards) {
        // They are finally here! We got {numCardsReceived}
        return CardVariant.Success;
    }
    if (altinn.isWaitingForAltinn) {
        return CardVariant.Spinner;
    }
    if (taxcards.error || altinn.error) {
        return CardVariant.Error;
    }
    if (taxcards.data) {
        return CardVariant.Normal; //This is normal - I think success should only be after you clicked to update and got something back
    }

    return CardVariant.Unknown;
}
